import { Note, NoteCreateRequest, NoteUpdateRequest } from '../../../../../types/api';
import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useResolveNoteMutation,
  useUnresolveNoteMutation,
  useUpdateNoteDateMutation,
  useUpdateNoteContentMutation,
} from '../../statistics.mutations';

// Note write operations (STD-015/016). Backed by TanStack mutations whose
// onSuccess invalidates `statisticsKeys.notes(droneSerial)` — that invalidation
// (not the former `window` CustomEvent bus) is what refreshes any open notes
// query. The optional callbacks are preserved for non-cache side effects
// (e.g. closing a dialog); they no longer drive cache refresh.
export const useNoteOperations = (
  droneSerial: string,
  onNoteCreated?: (note: Note) => void,
  onNoteUpdated?: (note: Note) => void,
  onNoteDeleted?: (noteId: string) => void
) => {
  const createMutation = useCreateNoteMutation(droneSerial);
  const updateMutation = useUpdateNoteMutation(droneSerial);
  const deleteMutation = useDeleteNoteMutation(droneSerial);
  const resolveMutation = useResolveNoteMutation(droneSerial);
  const unresolveMutation = useUnresolveNoteMutation(droneSerial);
  const updateDateMutation = useUpdateNoteDateMutation(droneSerial);
  const updateContentMutation = useUpdateNoteContentMutation(droneSerial);

  const createNote = async (noteData: NoteCreateRequest): Promise<Note> => {
    const newNote = await createMutation.mutateAsync(noteData);
    onNoteCreated?.(newNote);
    return newNote;
  };

  const updateNote = async (noteId: string, updates: NoteUpdateRequest): Promise<Note> => {
    const updatedNote = await updateMutation.mutateAsync({ noteId, updates });
    onNoteUpdated?.(updatedNote);
    return updatedNote;
  };

  const deleteNote = async (noteId: string): Promise<void> => {
    await deleteMutation.mutateAsync(noteId);
    onNoteDeleted?.(noteId);
  };

  const resolveNote = async (noteId: string, resolutionNotes?: string): Promise<Note> => {
    const resolvedNote = await resolveMutation.mutateAsync({ noteId, resolutionNotes });
    onNoteUpdated?.(resolvedNote);
    return resolvedNote;
  };

  const unresolveNote = async (noteId: string): Promise<Note> => {
    const unresolvedNote = await unresolveMutation.mutateAsync(noteId);
    onNoteUpdated?.(unresolvedNote);
    return unresolvedNote;
  };

  const updateNoteDate = async (noteId: string, dateOfAction: string): Promise<Note> => {
    const updatedNote = await updateDateMutation.mutateAsync({ noteId, dateOfAction });
    onNoteUpdated?.(updatedNote);
    return updatedNote;
  };

  const updateNoteContent = async (
    noteId: string,
    subject?: string,
    content?: string
  ): Promise<Note> => {
    const updatedNote = await updateContentMutation.mutateAsync({ noteId, subject, content });
    onNoteUpdated?.(updatedNote);
    return updatedNote;
  };

  return {
    createNote,
    updateNote,
    deleteNote,
    resolveNote,
    unresolveNote,
    updateNoteDate,
    updateNoteContent,
  };
};
